import {
  Employee,
  employeeFields,
  EmployeeGroup,
  Invite,
  InvitePayload,
  PlatformAccess,
} from '@fc-core/models';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { User } from '@fc-core';
import { ApiService, BaseApiService } from '@fc-core/services/base-api.service';
import { map } from 'rxjs/operators';
import { Feature } from '@fc-core/feature-flags/feature.model';
import { environment } from '../../../environments/environment';
import { loadAllRequests } from '@fc-core/services/load-all-requests';

export interface InviteInfo {
  customerId: string;
  customerName: string;
  employeeEmail: string;
  employeeFirstName: string;
  employeeLastName: string;
  requiredLogin: boolean;
  platformAccess: PlatformAccess[];
}

// todo split into two services
@ApiService({ baseName: 'users' })
@Injectable({ providedIn: 'root' })
export class UsersService extends BaseApiService {
  currentUser: User;

  constructor(private http: HttpClient) {
    super();
  }

  public loadUsers(): Observable<User[]> {
    return this.http
      .get<{ results: User[] }>(this.getUrl())
      .pipe(map((raw: { results: User[] }) => raw.results));
  }

  loadAllUsers(): Observable<{ list: User[]; count: number }> {
    return loadAllRequests(this.getUrl(), this.http);
  }

  public updateEmployee(id: string, props: any): Observable<Employee> {
    return this.http.patch<Employee>(
      environment.apiUrl + 'api/employees/' + id + '/',
      props,
    );
  }

  public updateUser(
    id: string,
    props: { email?: string; currentPassword?: string } & Partial<User>,
  ): Observable<User> {
    return this.http.patch<User>(this.getUrl(id), props);
  }

  public loadCurrentUser(id: string): Observable<User> {
    return this.http.get(this.getUrl(id)).pipe(
      map((user: User): User => {
        this.currentUser = user;
        return user;
      }),
    );
  }

  loadInvites(): Observable<{
    list: Invite[];
    count: number;
  }> {
    return loadAllRequests(`${this.getBaseUrl()}/invitation/`, this.http);
  }

  public deleteEmployee(id: string): Observable<string> {
    return this.http.delete<string>(`${this.getBaseUrl()}/employees/${id}/`);
  }

  public acceptInvitation(value: {
    key: string;
    firstName?: string;
    lastName?: string;
    password?: string;
  }): Observable<any> {
    return this.http.post(`${this.getBaseUrl()}/invitation/accept/`, value);
  }

  validateInvite(key: string): Observable<InviteInfo> {
    return this.http.post<InviteInfo>(
      `${this.getBaseUrl()}/invitation/validate/`,
      { key },
    );
  }

  public loadUserCollections(): Observable<EmployeeGroup[]> {
    return this.http
      .get(`${this.getBaseUrl()}/employee-groups/`)
      .pipe(map((resp: any) => resp['results']));
  }

  public createUserCollection(
    collection: EmployeeGroup,
  ): Observable<EmployeeGroup> {
    return this.http.post<EmployeeGroup>(
      `${this.getBaseUrl()}/employee-groups/`,
      collection,
    );
  }

  public updateUserCollection(
    collection: EmployeeGroup,
  ): Observable<EmployeeGroup> {
    return this.http.put<EmployeeGroup>(
      `${this.getBaseUrl()}/employee-groups/${collection.id}/`,
      collection,
    );
  }

  public deleteUserCollection(groupId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.getBaseUrl()}/employee-groups/${groupId}/`,
    );
  }

  public addEmployeeToGroup(
    groupId: number,
    employees: string[],
  ): Observable<EmployeeGroup> {
    return this.http.post<EmployeeGroup>(
      `${this.getBaseUrl()}/employee-groups/${groupId}/add/`,
      { employees },
    );
  }

  public removeEmployeeFromGroup(
    groupId: number,
    employees: string[],
  ): Observable<EmployeeGroup> {
    return this.http.post<EmployeeGroup>(
      `${this.getBaseUrl()}/employee-groups/${groupId}/remove/`,
      { employees },
    );
  }

  resendInvitation(
    inviteId: number,
    employee: string,
  ): Observable<{ sent: boolean }> {
    return this.http.post<{ sent: boolean }>(
      `${this.getBaseUrl()}/invitation/${inviteId}/send/`,
      { employee },
    );
  }

  public resendUserPassword(userEmail: string): Observable<{ detail: string }> {
    return this.http.post<{ detail: string }>(
      `${this.getBaseUrl()}/auth/password/reset/`,
      { email: userEmail },
    );
  }

  enableOperatorForEmployee(
    employeeId: string,
  ): Observable<{ id: number; employee: string }> {
    return this.http.post<{ id: number; employee: string }>(
      `${this.getBaseUrl()}/operators/`,
      { employee: employeeId },
    );
  }

  disableOperatorForEmployee(operatorId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.getBaseUrl()}/operators/${operatorId}/`,
    );
  }

  public getFeatures(): Observable<Feature[]> {
    return this.http.get<Feature[]>(environment.apiUrl + 'api/feature/');
  }

  addEmployee(invitePayload: InvitePayload): Observable<Employee> {
    return this.http.post<Employee>(environment.apiUrl + 'api/employees/', {
      firstName: invitePayload.firstName,
      lastName: invitePayload.lastName,
      primaryPhoneNumber: invitePayload.primaryPhoneNumber,
      timezone: invitePayload.timezone,
      groups: invitePayload.groups,
      email: invitePayload.email,
      isOperator: invitePayload.isOperator,
      isAdmin: invitePayload.isAdmin,
      streetAddress: invitePayload.streetAddress,
      postalCode: invitePayload.postalCode,
      platformAccess: invitePayload.platformAccess,
      city: invitePayload.city,
      state: invitePayload.state,
      website: invitePayload.website,
      description: invitePayload.description,
    });
  }

  loadEmployees(): Observable<{ list: Employee[]; count: number }> {
    const employeeListFields = employeeFields.filter(
      (field) => field !== 'permissions',
    );
    const params = new HttpParams().set('fields', employeeListFields.join(','));
    return loadAllRequests<Employee>(
      environment.apiUrl + 'api/employees/',
      this.http,
      params,
    );
  }

  loadEmployee(id: string): Observable<Employee> {
    const params = new HttpParams().set('fields', employeeFields.join(','));
    return this.http.get<Employee>(
      environment.apiUrl + `api/employees/${id}/`,
      { params },
    );
  }

  unarchiveEmployee(employeeId: string): Observable<void> {
    return this.http.get<void>(
      environment.apiUrl + `api/employees/${employeeId}/unarchive/`,
    );
  }
}
